/**
 * /events/:eventId — full-page card view for an event.
 *
 * Legacy parity URL shape (3mote-fe `src/router.jsx`:143). Events
 * have no project shorthand prefix — the URL is keyed by id directly
 * (legacy used `eventId`; we mirror that name for parity).
 */

import { createFileRoute } from '@tanstack/react-router'
import { useCallback, useEffect } from 'react'

import { getAccessToken } from '@/auth/cookie-helpers'
import { CardDetailPage, type CardPageSearch } from '@/features/board/components/CardDetailPage'

export const Route = createFileRoute('/events/$eventId')({
  component: EventDeepLinkRoute,
  validateSearch: (search: Record<string, unknown>): CardPageSearch => ({
    commentId: typeof search.commentId === 'string' ? search.commentId : undefined,
    parentCommentId:
      typeof search.parentCommentId === 'string' ? search.parentCommentId : undefined,
  }),
})

function EventDeepLinkRoute() {
  const navigate = Route.useNavigate()
  const { commentId, parentCommentId } = Route.useSearch()
  const stripCommentId = useCallback(
    () =>
      navigate({
        search: (prev) => ({ ...prev, commentId: undefined, parentCommentId: undefined }),
        replace: true,
      }),
    [navigate],
  )
  const { eventId } = Route.useParams()

  useEffect(() => {
    if (!getAccessToken()) {
      navigate({ to: '/login' })
    }
  }, [navigate])

  if (!getAccessToken()) return null

  return (
    <CardDetailPage
      kind="event"
      id={eventId}
      commentId={commentId}
      parentCommentId={parentCommentId}
      onCommentIdConsumed={stripCommentId}
    />
  )
}
